/**
 * Виджет «Рельеф» (карточка поля v3, вкладка «Обзор», PR-B).
 *
 * Ср./макс. уклон + экспозиция + «подробнее →» (открывает полный подэкран
 * рельефа). Данные — общий хук useFieldTerrain (GET /fields/{id}/terrain),
 * тот же кэш, что подэкран. Три исхода бэка (computed / not_ready / not_found)
 * различаем и показываем спокойным состоянием, а не ошибкой.
 *
 * ⚠️ Stealth-AI: только «Рельеф», «Уклон», «Экспозиция» — без «AI/вычислено».
 */
import { ChevronRight, Mountain } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { TileGrid } from '@/components/ui/tile-grid';
import { useFieldTerrain } from '@/lib/fields/useFieldTerrain';
import { formatSlope, aspectShort } from './widgetLogic';

// Плитка ряда TileGrid: высоту выравнивает грид (auto-rows-fr), собственный
// h-full/flex-1 не нужен — иначе ряд «плывёт» при разной длине подписи.
function Cell({ value, label }: { value: string; label: string }) {
  return (
    <div className="rounded-lg bg-muted/60 p-3 text-center">
      <div className="text-base font-semibold leading-6 tabular-nums">{value}</div>
      <div className="text-xs text-muted-foreground">{label}</div>
    </div>
  );
}

export function ReliefWidget({
  fieldId,
  onOpenDetails,
}: {
  fieldId: string;
  onOpenDetails: () => void;
}) {
  const { data, isLoading, isError } = useFieldTerrain(fieldId);
  const computed = data?.kind === 'computed';

  return (
    <Card>
      <CardHeader className="p-4 pb-3">
        <div className="flex items-center gap-2">
          <CardTitle className="flex items-center gap-2 leading-6">
            <Mountain className="h-4 w-4 text-muted-foreground" />
            Рельеф
          </CardTitle>
          {computed && (
            <Button
              variant="ghost"
              size="sm"
              className="ml-auto h-8 px-2 text-primary"
              onClick={onOpenDetails}
            >
              подробнее
              <ChevronRight className="h-4 w-4" />
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="p-4 pt-0">
        {isLoading ? (
          <TileGrid cols={3}>
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} className="h-16" />
            ))}
          </TileGrid>
        ) : isError || !data ? (
          <div className="rounded-lg border border-dashed border-border p-4 text-center text-sm text-muted-foreground">
            Данные рельефа недоступны.
          </div>
        ) : data.kind === 'computed' ? (
          <TileGrid cols={3}>
            <Cell value={formatSlope(data.metrics.slope_mean)} label="ср. уклон" />
            <Cell value={formatSlope(data.metrics.slope_max)} label="макс. уклон" />
            <Cell value={aspectShort(data.metrics.aspect_cardinal)} label="экспозиция" />
          </TileGrid>
        ) : (
          <div className="rounded-lg border border-dashed border-border p-4 text-center text-sm text-muted-foreground">
            {data.kind === 'not_ready' && data.status === 'pending'
              ? 'Рельеф считается — данные появятся позже.'
              : data.kind === 'not_ready' && data.status === 'no_reference_contour'
                ? 'Утвердите контур поля, чтобы получить рельеф.'
                : 'Данные рельефа пока недоступны.'}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default ReliefWidget;
